import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import AdminLayout from "@/components/admin/AdminLayout"
import { getAdminProductDetail } from "@/api/adminProductApi"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"

// 💡 백엔드 응답 형태에 맞춘 타입 정의
type AdminProductDetail = {
    productId: number
    categoryId: number
    categoryName: string
    productName: string
    productCode: string
    brandName: string
    summary: string
    description: string
    price: number
    discountPrice: number
    stockQuantity: number
    status: string
    visibleYn: string
    thumbnailUrl: string
    imageUrls: string[]
    createdAt: string
}

const formatPrice = (value: number) => `${value.toLocaleString("ko-KR")}원`

export default function AdminProductDetailPage() {
    const navigate = useNavigate()
    const { id } = useParams() // URL 파라미터에서 상품 ID 추출

    const [product, setProduct] = useState<AdminProductDetail | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const fetchProductDetail = async () => {
            if (!id) return;
            try {
                setLoading(true)
                const response = await getAdminProductDetail(id)
                // ApiResponse 구조 (success, data)에 맞게 처리
                if (response.success || response.data) {
                    setProduct(response.data || response)
                }
            } catch (error) {
                console.error(error)
                alert("상품 정보를 불러오는데 실패했습니다.")
                navigate("/admin/products")
            } finally {
                setLoading(false)
            }
        }
        fetchProductDetail()
    }, [id, navigate])

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex h-[50vh] items-center justify-center">
                    <p className="text-slate-500">상품 정보를 불러오는 중...</p>
                </div>
            </AdminLayout>
        )
    }

    if (!product) return null;

    return (
        <AdminLayout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-3xl font-bold">상품 상세 정보</h2>
                        <p className="mt-2 text-sm text-slate-500">
                            등록된 상품의 세부 정보와 이미지를 확인합니다.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" className="rounded-xl h-10 px-4" onClick={() => navigate("/admin/products")}>
                            목록으로
                        </Button>
                        <Button
                            className="rounded-xl h-10 px-4 bg-slate-900 hover:bg-slate-800 text-white"
                            onClick={() => navigate(`/admin/products/${id}/edit`)} // 💡 경로 추가
                        >
                            수정하기
                        </Button>
                    </div>
                </div>

                <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
                    {/* 좌측: 텍스트 정보 */}
                    <div className="space-y-6">
                        <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                            <CardHeader>
                                <CardTitle className="text-xl">기본 정보</CardTitle>
                            </CardHeader>
                            <CardContent className="grid gap-6 sm:grid-cols-2">
                                <div className="space-y-1">
                                    <Label className="text-slate-500">상품명</Label>
                                    <p className="text-base font-medium text-slate-900">{product.productName}</p>
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-slate-500">카테고리</Label>
                                    <p className="text-base font-medium text-slate-900">{product.categoryName}</p>
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-slate-500">상품코드</Label>
                                    <p className="text-base font-medium text-slate-900">{product.productCode || "-"}</p>
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-slate-500">브랜드명</Label>
                                    <p className="text-base font-medium text-slate-900">{product.brandName || "-"}</p>
                                </div>
                                <div className="space-y-1 sm:col-span-2">
                                    <Label className="text-slate-500">상품 요약</Label>
                                    <p className="text-base font-medium text-slate-900">{product.summary || "-"}</p>
                                </div>
                                <div className="space-y-1 sm:col-span-2">
                                    <Label className="text-slate-500">상세 설명</Label>
                                    <div className="rounded-xl bg-slate-50 p-4 whitespace-pre-wrap text-sm text-slate-700 min-h-[100px]">
                                        {product.description || "상세 설명이 없습니다."}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                            <CardHeader>
                                <CardTitle className="text-xl">판매 및 재고 정보</CardTitle>
                            </CardHeader>
                            <CardContent className="grid gap-6 sm:grid-cols-3">
                                <div className="space-y-1">
                                    <Label className="text-slate-500">판매가</Label>
                                    <p className="text-lg font-semibold text-slate-900">{formatPrice(product.price)}</p>
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-slate-500">할인가</Label>
                                    <p className="text-lg font-semibold text-rose-600">
                                        {product.discountPrice ? formatPrice(product.discountPrice) : "-"}
                                    </p>
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-slate-500">재고 수량</Label>
                                    <p className="text-base font-medium text-slate-900">{product.stockQuantity}개</p>
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-slate-500">상태</Label>
                                    <p className="mt-1">
                                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                                            {product.status === "SELLING" ? "판매중" : product.status === "SOLD_OUT" ? "품절" : "숨김"}
                                        </span>
                                    </p>
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-slate-500">노출 여부</Label>
                                    <p className="text-base font-medium text-slate-900">{product.visibleYn === "Y" ? "노출" : "비노출"}</p>
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-slate-500">등록 일시</Label>
                                    <p className="text-sm font-medium text-slate-700">{product.createdAt}</p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* 우측: 이미지 정보 */}
                    <div className="space-y-6">
                        <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                            <CardHeader>
                                <CardTitle className="text-xl">이미지 정보</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="space-y-3">
                                    <Label className="text-slate-500">대표 이미지</Label>
                                    {product.thumbnailUrl ? (
                                        <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50 aspect-square">
                                            <img src={product.thumbnailUrl} alt="대표 이미지" className="w-full h-full object-cover" />
                                        </div>
                                    ) : (
                                        <div className="flex h-32 items-center justify-center rounded-xl bg-slate-50 text-sm text-slate-400 border border-slate-200">
                                            이미지 없음
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-3">
                                    <Label className="text-slate-500">상세 이미지 ({product.imageUrls?.length || 0}장)</Label>
                                    <div className="grid gap-3 grid-cols-2">
                                        {product.imageUrls && product.imageUrls.length > 0 ? (
                                            product.imageUrls.map((url, index) => (
                                                <div key={index} className="relative overflow-hidden rounded-xl border border-slate-200 bg-slate-50 aspect-square">
                                                    <span className="absolute top-2 left-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-md z-10">
                                                        {index + 1}
                                                    </span>
                                                    <img src={url} alt={`상세 이미지 ${index + 1}`} className="w-full h-full object-cover" />
                                                </div>
                                            ))
                                        ) : (
                                            <div className="col-span-2 flex h-24 items-center justify-center rounded-xl bg-slate-50 text-sm text-slate-400 border border-slate-200">
                                                상세 이미지가 없습니다.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </AdminLayout>
    )
}